import React, { useState, useEffect } from 'react';
import {
  DateRangePicker,
  IconButton,
  SelectPicker,
  Table,
  Pagination
} from 'rsuite';
import { addDays } from 'date-fns';
import moment from 'moment';
import autoTable from 'jspdf-autotable';
import { jsPDF } from 'jspdf';
import { RenderIf } from 'utilities/helpers';
// import { OrderAreaChart } from "@/components/charts/orderareachart";
import { useFetchOrdersHook } from 'hooks/orders/useFetchOrdersHook';
import { CustomTable } from 'components/datatable';
import CollaspedOutlineIcon from '@rsuite/icons/ArrowUpLine';
import ExpandOutlineIcon from '@rsuite/icons/ArrowDownLine';
import momentTZ from 'moment-timezone';
import styles from './index.module.css';
import { DashboardAreaChartBig } from 'components/charts/dashboardareachartbig';
import { Loading } from 'components/loading';
import { Ikon } from 'assets/icons';
import { useCountry } from 'contexts/CountryContext';

const { Column, HeaderCell, Cell } = Table;

const predefinedRanges = [
  {
    label: 'Today',
    value: [new Date(), new Date()],
    placement: 'left'
  },
  {
    label: 'Yesterday',
    value: [addDays(new Date(), -1), addDays(new Date(), -1)],
    placement: 'left'
  },
  {
    label: 'Last week',
    value: [addDays(new Date(), -7), new Date()],
    placement: 'left'
  },
  {
    label: 'Last month',
    value: [addDays(new Date(), -30), new Date()],
    placement: 'left'
  },
  {
    label: 'Last year',
    value: [addDays(new Date(), -364), new Date()],
    placement: 'left'
  }
];

const orderStatusOptions = [
  {
    label: 'All statuses',
    value: ''
  },
  {
    label: 'Pending',
    value: 'PENDING'
  },
  {
    label: 'Completed',
    value: 'COMPLETED'
  },
  {
    label: 'Cancelled',
    value: 'CANCELLED'
  }
];

const paymentStatusOptions = [
  {
    label: 'All payment statuses',
    value: ''
  },
  {
    label: 'Pending',
    value: 'PENDING'
  },
  {
    label: 'Completed',
    value: 'COMPLETED'
  },
  {
    label: 'Cancelled',
    value: 'CANCELLED'
  },
  {
    label: 'Refunded',
    value: 'REFUNDED'
  },
  {
    label: 'Expired',
    value: 'EXPIRED'
  }
];

const paymentMethodOptions = [
  {
    label: 'All payment methods',
    value: ''
  },
  {
    label: 'Cash',
    value: 'CASH'
  },
  {
    label: 'Terminal',
    value: 'TERMINAL'
  },
  {
    label: 'Card',
    value: 'CARD'
  },
  {
    label: 'Payment Link',
    value: 'PAYMENT_LINK'
  },
  {
    label: 'Other',
    value: 'OTHER'
  }
];

const orderViaOptions = [
  {
    label: 'All channels',
    value: ''
  },
  {
    label: 'Zewst Online',
    value: 'ZEWST_ONLINE'
  },
  {
    label: 'Zewst App',
    value: 'ZEWST_APP'
  },
  {
    label: 'Zewst POS',
    value: 'ZEWST_POS'
  },
  {
    label: 'External POS',
    value: 'EXTERNAL_POS'
  },
  {
    label: 'Zewst Super Admin',
    value: 'ZEWST_SUPER_ADMIN'
  },
  {
    label: 'Zewst IVR',
    value: 'ZEWST_IVR'
  },
  {
    label: 'Other',
    value: 'OTHER'
  }
];

const orderTypeOptions = [
  {
    label: 'All types order',
    value: ''
  },
  {
    label: 'Delivery',
    value: 'DELIVERY'
  },
  {
    label: 'Dine In',
    value: 'DINE_IN'
  },
  {
    label: 'Take Away',
    value: 'TAKE_AWAY'
  },
  {
    label: 'Other',
    value: 'OTHER'
  }
];

const orderPerPageOptions = [
  {
    label: '10',
    value: '10'
  },
  {
    label: '20',
    value: '20'
  },
  {
    label: '50',
    value: '50'
  },
  {
    label: '100',
    value: '100'
  }
];

const OrderFiltersComponent = ({
  brandId,
  branchId,
  initialFromDate = '2024-03-01',
  initialToDate = '2024-03-31'
}) => {
  const { formatCurrency, formatDate, dateFormat } = useCountry();
  const pickerFormat = dateFormat === 'DD/MM/YYYY' ? 'dd/MM/yyyy' : dateFormat === 'DD-MM-YYYY' ? 'dd-MM-yyyy' : 'MM/dd/yyyy';
  // State for date range
  const [fromDate, setFromDate] = useState(initialFromDate);
  const [toDate, setToDate] = useState(initialToDate);

  // State for pagination
  const [_, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // State for all filter options
  const [filters, setFilters] = useState({
    // Initialize with default values or empty
    orderStatus: undefined,
    paymentStatus: undefined,
    paymentMethod: undefined,
    orderVia: undefined,
    orderType: undefined,
    customerName: '',
    page: 1,
    limit: 20
  });

  // Function to update a single filter
  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  // Function to clear all filters
  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: pageSize
    });
    setCurrentPage(1);
  };

  // Use the hook with current filters
  const { ordersData, ordersLoading, ordersError, ordersRefetch } =
    useFetchOrdersHook(
      'orders-list',
      brandId,
      branchId,
      fromDate,
      toDate,
      filters
    );

  const Orders = ordersData?.orders
    ? ordersData?.orders?.sort(
        (a, b) =>
          new Date(b?.orderedOn).getTime() - new Date(a?.orderedOn).getTime()
      )
    : [];

  const salesAreaChartData = ordersData?.dailySales || [];

  const handleDateRangePicker = (date) => {
    setFromDate(moment(date?.[0]).format('YYYY-MM-DD'));
    setToDate(moment(date?.[1]).format('YYYY-MM-DD'));
  };

  const grossSales = ordersData?.grossSales?.toFixed(2) || 0;

  const fulfilledOrders = ordersData?.fulfilledOrders || 0;

  const failedOrders = ordersData?.failedOrders || 0;

  const kdsCompletedOrders = ordersData?.kdsCompletedOrders || 0;

  const handleExport = () => {
    // Create new document without type assertion
    const doc = new jsPDF();

    // Title and filters info
    doc.setFontSize(20);
    doc.setTextColor(38, 38, 38);
    doc.text('Orders Report', 14, 20);

    doc.setFontSize(12);
    doc.setTextColor(102, 102, 102);
    doc.text(
      `Period: ${formatDate(fromDate) || formatDate(moment().subtract(1, 'month').toDate())} - ${formatDate(toDate) || formatDate(moment().toDate())}`,
      14,
      30
    );

    // Stats section with updated metrics
    const statsData = [
      ['Gross Sales', `$${grossSales}`],
      ['Total Orders Volume', ordersData?.totalOrders?.toString() || '0'],
      ['Fulfilled Orders', fulfilledOrders?.toString() || '0'],
      ['Failed Orders', failedOrders?.toString() || '0'],
      ['KDS Completed Orders', kdsCompletedOrders?.toString() || '0']
    ];

    // Add autotable to the document
    autoTable(doc, {
      head: [['Metric', 'Value']],
      body: statsData,
      startY: 40,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [151, 71, 255] },
      theme: 'grid'
    });

    // Orders table
    const tableHeaders = [
      ['Order Code', 'Customer', 'Status', 'Order Type', 'Date', 'Total']
    ];

    const tableData = Orders.map((order) => [
      order.orderCode,
      order.customer?.fullName?.replace(':', ' ') || '-',
      order.payment,
      order.type?.replace('_', ' ') || '-',
      formatDate(order.orderedOn),
      formatCurrency(order.total)
    ]);

    // Second table
    autoTable(doc, {
      head: tableHeaders,
      body: tableData,
      startY: 120,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [151, 71, 255] },
      theme: 'grid'
    });

    doc.save('orders-report.pdf');
  };

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const handleExpanded = (rowData, dataKey) => {
    let open = false;
    const nextExpandedRowKeys = [];

    expandedRowKeys.forEach((key) => {
      if (key === rowData[rowKey]) {
        open = true;
      } else {
        nextExpandedRowKeys.push(key);
      }
    });

    if (!open) {
      nextExpandedRowKeys.push(rowData[rowKey]);
    }

    setExpandedRowKeys(nextExpandedRowKeys);
  };

  const renderRowExpanded = (rowData) => {
    const formatCurrencyLocal = (amount) => {
      return formatCurrency(amount);
    };

    return (
      <div className={styles.rowExpandBx}>
        <div
          className={styles.expandItemBx}
          style={{
            overflowY: 'scroll'
          }}
        >
          <h6>Order Items</h6>
          {rowData?.itemDetails?.map((item, i) => {
            const quantity =
              rowData?.items?.find((i) => item?._id === i?.itemId)?.quantity ||
              0;
            const totalPrice = (item?.price || 0) * quantity;

            return (
              <div className={styles.item} key={i}>
                <div>
                  <img
                    src={item?.image || '/placeholder-food.png'}
                    alt={item?.name || item?.item || 'Food item'}
                    onError={(e) => {
                      e.target.src = '/placeholder-food.png';
                    }}
                  />
                  <h6>
                    {quantity}x {item?.item || item?.name || 'Unknown Item'}
                  </h6>
                </div>
                <h6>{formatCurrencyLocal(totalPrice)}</h6>
              </div>
            );
          })}
        </div>
        <RenderIf isTrue={!rowData?.customer?.guest}>
          <div className={styles.expandItemBx}>
            <h6>Customer Details</h6>
            <div className={styles.item}>
              <p>Name:</p>
              <h6>{rowData?.customer?.fullName || 'N/A'}</h6>
            </div>
            <div className={styles.item}>
              <p>Phone:</p>
              <h6>{rowData?.customer?.contact?.[0]?.contactNumber || 'N/A'}</h6>
            </div>
            <div className={styles.item}>
              <p>Email:</p>
              <h6
                style={{
                  fontSize: '10px'
                }}
              >
                {rowData?.customer?.email || 'N/A'}
              </h6>
            </div>
          </div>
        </RenderIf>
        <div className={styles.expandItemBx}>
          <h6>Price Breakdown</h6>
          <div className={styles.item}>
            <p>Subtotal:</p>
            <h6>{formatCurrencyLocal(rowData?.subTotal)}</h6>
          </div>
          <div className={styles.item}>
            <p>Tax:</p>
            <h6>{formatCurrencyLocal(rowData?.tax)}</h6>
          </div>
          <div className={styles.item}>
            <p>Delivery Fee:</p>
            <h6>{formatCurrencyLocal(rowData?.deliveryFee)}</h6>
          </div>
          <div className={styles.item}>
            <p>Discount:</p>
            <h6>-{formatCurrencyLocal(rowData?.discount)}</h6>
          </div>
          <hr />
          <div className={styles.item}>
            <h6>Amount:</h6>
            <h6>{formatCurrencyLocal(rowData?.total)}</h6>
          </div>
        </div>
      </div>
    );
  };

  const ExpandCell = ({
    rowData,
    dataKey,
    expandedRowKeys,
    onChange,
    ...props
  }) => (
    <Cell {...props} style={{ padding: 5 }}>
      <IconButton
        appearance="subtle"
        onClick={() => {
          onChange(rowData);
        }}
        icon={
          expandedRowKeys.some((key) => key === rowData[rowKey]) ? (
            <CollaspedOutlineIcon />
          ) : (
            <ExpandOutlineIcon />
          )
        }
      />
    </Cell>
  );

  const OrderViaCell = ({ rowData, dataKey, ...props }) => {
    return (
      <Cell {...props}>
        <div
          style={{
            fontSize: 12,
            fontWeight: `500`
          }}
        >
          {rowData?.orderVia === `ZEWST_APP`
            ? `App`
            : rowData?.orderVia === `ZEWST_ONLINE`
            ? `Online store`
            : `-`}
        </div>
      </Cell>
    );
  };

  const OrderTypeCell = ({ rowData, dataKey, ...props }) => {
    return (
      <Cell {...props}>
        <div
          style={{
            width: 70,
            height: 20,
            borderRadius: 3,
            display: `flex`,
            alignItems: `center`,
            justifyContent: `center`,
            fontSize: 11,
            color: `rgba(88, 99, 111, 1)`,
            fontWeight: `600`,
            background:
              rowData?.orderType === `TAKE AWAY`
                ? `#F3E3D7`
                : rowData?.orderType === `DELIVERY`
                ? `#DDF3D7`
                : `#E4D7F3`
          }}
        >
          {rowData?.orderType === `TAKE AWAY`
            ? `Take Away`
            : rowData?.orderType === `DELIVERY`
            ? `Delivery`
            : `Dine in`}
        </div>
      </Cell>
    );
  };

  const OrderDateCell = ({ rowData, dataKey, ...props }) => {
    return <Cell {...props}>{rowData?.orderDate}</Cell>;
  };

  const OrderStatusCell = ({ rowData, dataKey, ...props }) => {
    return (
      <Cell {...props}>
        <div
          style={{
            width: 70,
            height: 20,
            borderRadius: 3,
            display: `flex`,
            alignItems: `center`,
            justifyContent: `center`,
            fontSize: 11,
            color: `rgba(88, 99, 111, 1)`,
            fontWeight: `600`,
            background:
              rowData?.orderStatus === `In Progress`
                ? `#F4D9C6`
                : rowData?.orderStatus === `Cancelled`
                ? `#EFD4D4`
                : `rgba(221, 243, 215, 1)`
          }}
        >
          {rowData?.orderStatus === `PENDING`
            ? `In-progress`
            : rowData?.orderStatus === `CANCELLED`
            ? `Cancelled`
            : rowData?.orderStatus}
        </div>
      </Cell>
    );
  };

  const rowKey = 'id';

  const pagination = ordersData?.orders?.length > 20 ? true : false;

  const handlePageChange = (value) => {
    setCurrentPage(value);
    updateFilter('page', value);
  };

  const handlePageSizeChange = (value) => {
    setPageSize(value);
    updateFilter('limit', value);
    updateFilter('page', 1);
  };

  console.log(ordersData);

  return (
    <div className="orders-filters-container">
      <header className={styles.header}>
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '800' }}>
            Orders History
          </h2>
          <h6>See all the details about your orders</h6>
        </div>
      </header>

      <div className={styles.filterBx}>
        <DateRangePicker
          placeholder="Start date - End date"
          onOk={(date) => handleDateRangePicker(date)}
          onChange={(date) => handleDateRangePicker(date)}
          showOneCalendar
          defaultValue={[
            new Date(moment().subtract(1, 'month').valueOf()),
            new Date(moment().valueOf())
          ]}
          ranges={predefinedRanges}
          className="dRangePickerInput autoWidth"
          format={pickerFormat}
        />
        <SelectPicker
          data={orderStatusOptions?.map((opt) => ({
            value: opt?.value,
            label: opt?.label
          }))}
          defaultValue={undefined}
          onChange={(value) => updateFilter('orderStatus', value || undefined)}
          style={{ fontSize: '12px' }}
          searchable={false}
          placeholder={'Order status'}
          className="dRangePickerInput autoWidth"
        />
        <SelectPicker
          data={paymentStatusOptions?.map((opt) => ({
            value: opt?.value,
            label: opt?.label
          }))}
          defaultValue={undefined}
          onChange={(value) =>
            updateFilter('paymentStatus', value || undefined)
          }
          style={{ fontSize: '12px' }}
          searchable={false}
          placeholder={'Payment status'}
          className="dRangePickerInput autoWidth"
        />
        <SelectPicker
          data={paymentMethodOptions?.map((opt) => ({
            value: opt?.value,
            label: opt?.label
          }))}
          defaultValue={undefined}
          onChange={(value) =>
            updateFilter('paymentMethod', value || undefined)
          }
          style={{ fontSize: '12px' }}
          searchable={false}
          placeholder={'Payment method'}
          className="dRangePickerInput autoWidth"
        />
        <SelectPicker
          data={orderViaOptions?.map((opt) => ({
            value: opt?.value,
            label: opt?.label
          }))}
          defaultValue={undefined}
          onChange={(value) => updateFilter('orderVia', value || undefined)}
          style={{ fontSize: '12px' }}
          searchable={false}
          placeholder={'Order channel'}
          className="dRangePickerInput autoWidth"
        />
        <SelectPicker
          data={orderTypeOptions?.map((opt) => ({
            value: opt?.value,
            label: opt?.label
          }))}
          defaultValue={undefined}
          onChange={(value) => updateFilter('orderType', value || undefined)}
          style={{ fontSize: '12px' }}
          searchable={false}
          placeholder={'Order type'}
          className="dRangePickerInput autoWidth"
        />
        <div>
          <button
            type={'button'}
            className={styles.clearBtn}
            onClick={() => {
              clearFilters();
            }}
          >
            Clear
          </button>
        </div>
      </div>

      <div className={styles.pageBx}>
        <RenderIf isTrue={ordersLoading}>
          <div
            style={{
              display: `flex`,
              justifyContent: `center`,
              alignItems: `center`,
              height: `100%`
            }}
          >
            <Loading />
          </div>
        </RenderIf>
        <RenderIf isTrue={!ordersLoading}>
          <header className={styles.header_stats}>
            <div>
              <div className={styles.statsItemContent}>
                <p>Gross sales</p>
                <h3>{formatCurrency(grossSales)}</h3>
              </div>
            </div>
            <div>
              <div className={styles.statsItemContent}>
                <p>Total orders volume</p>
                <h3>{ordersData?.totalOrders || 0}</h3>
              </div>
            </div>
            <div>
              <div className={styles.statsItemContent}>
                <p>Fulfilled orders</p>
                <h3>{fulfilledOrders}</h3>
              </div>
            </div>
            <div>
              <div className={styles.statsItemContent}>
                <p>Failed orders</p>
                <h3>{failedOrders}</h3>
              </div>
            </div>
            <div>
              <div className={styles.statsItemContent}>
                <p>KDS completed orders</p>
                <h3>{kdsCompletedOrders}</h3>
              </div>
            </div>
          </header>
          <div className={styles.flex_1}>
            <div className={styles.chartContainer}>
              <DashboardAreaChartBig
                data={salesAreaChartData}
                keyName={'dailySales'}
              />
            </div>
          </div>
          <div className={styles.tableBx}>
            <div className={styles.tableHeader}>
              <div className={styles.filterBx}>
                <h4>Orders List</h4>
                <div className={styles.inputBx}>
                  <input
                    type="text"
                    placeholder="Customer name"
                    value={filters.customerName || ''}
                    onChange={(e) =>
                      updateFilter('customerName', e.target.value || undefined)
                    }
                  />
                </div>
              </div>
              <button
                type={'button'}
                className={styles.exportButton}
                onClick={handleExport}
              >
                <Ikon name="download" fillColor="white" />
                Export PDF
              </button>
            </div>
            <RenderIf isTrue={ordersData?.orders?.length > 0}>
              <CustomTable
                rowHeight={52}
                rows={ordersData?.orders
                  ?.filter((order) => order != null)
                  ?.sort(
                    (a, b) => new Date(b?.orderedOn) - new Date(a?.orderedOn)
                  )
                  ?.map((order, index) => {
                    return {
                      id: index + 1,
                      customerEmail: order?.customer?.guest
                        ? 'Guest'
                        : order?.customer?.fullName?.replace(':', ' '),
                      orderTotal: order?.total?.toFixed(2),
                      orderId: order?.orderCode,
                      orderVia: order?.orderVia,
                      orderType: order?.type?.replace('_', ' '),
                      orderTime: momentTZ(order?.orderedOn)
                        .tz(momentTZ.tz.guess())
                        .format('hh:mm:ss A'),
                      orderDate: formatDate(order?.orderedOn),
                      orderStatus:
                        order?.payment === `PENDING`
                          ? `In Progress`
                          : order?.payment === `COMPLETED`
                          ? `Completed`
                          : `Cancelled`,
                      items: order?.items,
                      ...order
                    };
                  })}
                loading={ordersLoading}
                pageLimit={filters?.limit}
                pagination={pagination}
                renderRowExpanded={renderRowExpanded}
                expandedRowKeys={expandedRowKeys}
              >
                <Column width={40} align="center">
                  <HeaderCell></HeaderCell>
                  <ExpandCell
                    dataKey="id"
                    expandedRowKeys={expandedRowKeys}
                    onChange={handleExpanded}
                  />
                </Column>
                <Column width={120}>
                  <HeaderCell>Order Id</HeaderCell>
                  <Cell dataKey="orderId" />
                </Column>
                <Column width={110} sortable>
                  <HeaderCell>Order Total</HeaderCell>
                  <Cell dataKey="orderTotal" />
                </Column>
                <Column flexGrow={1} sortable>
                  <HeaderCell>Customer</HeaderCell>
                  <Cell dataKey="customerEmail" />
                </Column>
                <Column width={120} sortable>
                  <HeaderCell>Platform</HeaderCell>
                  <OrderViaCell dataKey="orderVia" />
                </Column>
                <Column width={90} sortable>
                  <HeaderCell>Type</HeaderCell>
                  <OrderTypeCell dataKey="orderType" />
                </Column>
                <Column width={130} sortable>
                  <HeaderCell>Time</HeaderCell>
                  <Cell dataKey="orderTime" />
                </Column>
                <Column width={130} sortable>
                  <HeaderCell>Date</HeaderCell>
                  <OrderDateCell dataKey="orderDate" />
                </Column>
                <Column width={90} sortable>
                  <HeaderCell>Status</HeaderCell>
                  <OrderStatusCell dataKey="orderStatus" />
                </Column>
              </CustomTable>
            </RenderIf>
            <RenderIf
              isTrue={!ordersLoading && ordersData?.orders?.length === 0}
            >
              <div className={styles.noData}>No Orders data found</div>
            </RenderIf>
            <RenderIf isTrue={ordersData?.orders?.length > 0}>
              {/* Pagination Component */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '16px',
                  padding: '0 16px'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    color: '#666'
                  }}
                >
                  <span>Show</span>
                  <SelectPicker
                    data={orderPerPageOptions?.map((opt) => ({
                      value: Number(opt?.value),
                      label: opt?.label
                    }))}
                    value={filters.limit}
                    onChange={handlePageSizeChange}
                    style={{ width: '80px', fontSize: '12px' }}
                    searchable={false}
                    cleanable={false}
                    size="sm"
                  />
                  <span>per page</span>
                </div>

                <Pagination
                  prev
                  next
                  first
                  last
                  ellipsis
                  boundaryLinks
                  maxButtons={5}
                  activePage={filters.page || 1}
                  pages={Math.ceil(
                    (ordersData?.totalOrders || 0) / (filters.limit || 20)
                  )}
                  total={ordersData?.totalOrders || 0}
                  limit={filters.limit || 20}
                  onSelect={handlePageChange}
                  size="sm"
                />

                <div
                  style={{
                    fontSize: '14px',
                    color: '#666',
                    minWidth: '150px',
                    textAlign: 'right'
                  }}
                >
                  {ordersData?.totalOrders ? (
                    <>
                      Showing{' '}
                      {((filters.page || 1) - 1) * (filters.limit || 20) + 1} to{' '}
                      {Math.min(
                        (filters.page || 1) * (filters.limit || 20),
                        ordersData.totalOrders
                      )}{' '}
                      of {ordersData.totalOrders} entries
                    </>
                  ) : (
                    'No entries found'
                  )}
                </div>
              </div>
            </RenderIf>
          </div>
        </RenderIf>
      </div>
    </div>
  );
};

export default OrderFiltersComponent;
